/**
 * @file 채널 좌측 사이드바 — 채널 목록 + <b>채널별 폴더 트리</b>.
 *
 * <h3>폴더 트리는 «선택» 과 무관하다</h3>
 * <p>어느 채널이든 행 오른쪽 ⌄ 로 폴더 목록을 열고 닫을 수 있고, 한 번 연 목록은 다른 채널로
 * 옮겨도 닫히지 않는다. 그래서 «다른 프로젝트에 뭐가 있는지» 를 그 채널에 들어가지 않고 볼 수 있다.
 * 상태는 {@code expandedFolders}(편 채널 id 집합) 하나로 관리하고, 폴더 데이터는 채널 목록 API 가
 * 모든 채널 몫을 한 번에 실어 온다({@code ChannelPage.foldersByChannel}).
 *
 * <h3>왜 파일을 따로 뺐나 — 애니메이션이 재생되지 않던 원인</h3>
 * <p>예전에는 {@code ChannelItem}·{@code FolderTree} 같은 하위 컴포넌트를 사이드바 <b>렌더 본문
 * 안에서</b> 정의했다. 그러면 부모가 리렌더될 때마다 컴포넌트 <i>타입</i>이 새로 만들어져 React 가
 * 서브트리를 통째로 unmount→mount 한다. 결과는 두 가지다:
 * <ol>
 *   <li><b>접기 애니메이션이 아예 재생되지 않는다</b> — 새로 mount 된 노드는 이미 최종 상태다.</li>
 *   <li>⋮ 메뉴 anchor 같은 지역 state 가 날아간다.</li>
 * </ol>
 * 메일 사이드바({@code MailPage.jsx} 의 {@code CustomFolderItem})가 같은 이유로 모듈 최상위에
 * 정의돼 있고 주석으로 경고해 뒀는데, 채널 쪽에서 그대로 밟았다. 이제 <b>모두 모듈 최상위</b>이며
 * 필요한 값은 {@code ctx} 하나로 받는다.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    Box, Divider, IconButton, Menu, MenuItem, Stack, Tooltip, Typography,
} from "@mui/material";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import CreateNewFolderRoundedIcon from "@mui/icons-material/CreateNewFolderRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import FolderSpecialRoundedIcon from "@mui/icons-material/FolderSpecialRounded";
import SubdirectoryArrowRightRoundedIcon from "@mui/icons-material/SubdirectoryArrowRightRounded";
import ActionButton from "../../component/common/ActionButton";
import CollapsibleSidebar from "../../component/common/CollapsibleSidebar";
import { ds } from "../../styles/designSystem";
import { ALL, UNFILED } from "./folder/folderScope";
import { daysUntil, ddayLabel, fmtDate, toLocalDate } from "./dday";
import { projectPhase } from "./channelPhase";

const C = ds.colors;

/**
 * 펼침 애니메이션 길이(ms). 채널을 고를 때 <b>짧게 열리는</b> 느낌만 주는 값이라 짧게 잡는다 —
 * 폴더 목록은 이미 손에 있어 기다림을 덮을 필요가 없고, 길면 그대로 지연으로 읽힌다.
 */
const COLLAPSE_MS = 140;

/**
 * 접기/펼치기 애니메이션 래퍼 — grid-template-rows 0fr↔1fr.
 *
 * <p><b>사이드바에서 접히는 것은 전부 이걸 쓴다</b> — 즐겨찾기 구간, 프로젝트의 네 하위 구간,
 * 채널별 폴더 트리. 예전엔 폴더 트리만 이 래퍼였고 구간들은 {@code {open && ...}} 로 그냥
 * 붙였다 떼는 식이라, 같은 사이드바 안에서 어떤 줄은 스르륵 열리고 어떤 줄은 툭 사라졌다.
 * 접기라는 <b>한 가지 동작</b>이 두 가지로 보이면 사용자는 둘을 다른 기능으로 읽는다.
 *
 * <p>MUI {@code Collapse} 를 쓰지 않는 이유는 메일 사이드바와 같다: Collapse 는 자기 콘텐츠 높이를
 * <b>재서</b> 인라인 height 로 박아 넣는다. 사이드바처럼 중첩되면(구간 → 채널 → 폴더) 바깥이 높이를
 * 재는 시점에 안쪽이 아직 0 이라 실제보다 작은 값으로 굳고, 애니메이션이 끝나 overflow:visible 이
 * 되는 순간 하위 항목이 아래 섹션 위로 새어 나온다. 이 방식은 높이를 재지 않아 안전하다.
 *
 * <h4>마운트할 때도 열림이 재생된다</h4>
 * <p>CSS 전이는 <b>값이 바뀌어야</b> 재생된다. 폴더가 채널 목록에 실려 오면서 이 래퍼는 채널을 고른
 * 그 순간 이미 {@code open=true} 로 마운트되는데, 그러면 처음부터 1fr 이라 전이가 일어날 일이 없다
 * (= 툭 나타난다). 그래서 <b>항상 0fr 로 마운트한 뒤</b> 다음 프레임에 열어 준다.
 *
 * <p>rAF 를 두 번 겹치는 이유: 한 번만 쓰면 0fr 이 화면에 <b>그려지기 전에</b> 1fr 로 바뀌어
 * 브라우저가 두 값을 한 번의 페인트로 합쳐 버린다(= 전이가 또 안 보인다). 두 번째 rAF 는
 * 0fr 이 실제로 한 번 그려진 뒤에 도는 것이 보장된다.
 *
 * <p>{@code entered} 는 한 번 true 가 되면 그대로다 — 사용자가 ⌄ 로 직접 접었다 펴는 동작은
 * 이 지연을 타지 않고 곧바로 전이된다.
 */
const CollapseRows = ({ open, children }) => {
    const [entered, setEntered] = useState(false);

    useEffect(() => {
        let inner;
        const outer = requestAnimationFrame(() => {
            inner = requestAnimationFrame(() => setEntered(true));
        });
        return () => {
            cancelAnimationFrame(outer);
            if (inner) cancelAnimationFrame(inner);
        };
    }, []);

    return (
        <Box sx={{
            display: "grid",
            gridTemplateRows: open && entered ? "1fr" : "0fr",
            transition: `grid-template-rows ${COLLAPSE_MS}ms ease`,
            flexShrink: 0,
        }}>
            <Box sx={{ overflow: "hidden", minHeight: 0 }}>{children}</Box>
        </Box>
    );
};

/**
 * 최상위 유형(즐겨찾기 / 프로젝트 / 기타) 사이 경계.
 *
 * <p>구간 사이 선({@code borderLight})보다 <b>진하고 여백도 두 배</b>다. 두 종류의 선이
 * 같은 두께·같은 간격이면 "프로젝트 안의 종료" 와 "프로젝트 다음의 기타" 가 같은 층으로 읽혀
 * 채널 유형 구분이 사라진다. 여기서 한 단계 크게 끊어 준다.
 */
/**
 * 최상위 유형(즐겨찾기 / 프로젝트 / 기타) 사이 경계.
 *
 * <p>구간 사이 선({@code borderLight})보다 <b>진하고 여백도 두 배</b>다. 두 종류의 선이
 * 같은 두께·같은 간격이면 "프로젝트 안의 종료" 와 "프로젝트 다음의 기타" 가 같은 층으로 읽혀
 * 채널 유형 구분이 사라진다. 여기서 한 단계 크게 끊어 준다.
 */
const TypeBoundary = () => (
    <Divider sx={{ mx: 2, mt: 3, mb: 2, borderColor: C.border }} />
);

/**
 * 섹션 헤더 — 라벨은 <b>가장 왼쪽</b>, 토글(꺾쇠)은 <b>가장 오른쪽</b>.
 *
 * <p>예전엔 꺾쇠가 라벨 앞에 붙어 헤더가 그 폭만큼 밀려났고, 정작 그 아래 항목은 덜 들여써져
 * <b>부모가 자식보다 더 안쪽</b>에 있는 역전이 생겼다(읽을 때 계층이 뒤집혀 보인다).
 * 토글을 오른쪽 끝으로 빼면 라벨의 왼쪽 기준선이 고정되고, 들여쓰기는 아래 항목만 갖는다.
 *
 * <p>즐겨찾기 헤더는 이 규칙에서 <b>혼자 빠져 있었다</b> — 직접 만든 Box 에 꺾쇠를 라벨 앞에
 * 붙이고 있어서, 세 구간 중 그 라벨만 오른쪽으로 밀려 있었다. 이제 셋 다 이 컴포넌트를 쓴다.
 *
 * <p><b>라벨 앞에는 아무것도 두지 않는다.</b> 잠시 즐겨찾기에 ★ 를 붙일 수 있게 열어 뒀는데,
 * 꺾쇠를 오른쪽으로 뺀 것과 같은 이유로 결국 그 라벨만 아이콘 폭만큼 밀렸다 — 앞에 무엇이든
 * 놓는 순간 세 구간의 시작점이 어긋난다. 구간 표식이 필요하면 <b>오른쪽</b>에 둘 것.
 */
const SectionHeader = ({ label, count, toggle, expanded }) => (
    <Box
        onClick={toggle}
        sx={{
            display: "flex", alignItems: "center", gap: 0.75, px: 2, pt: 2, pb: 0.5,
            cursor: toggle ? "pointer" : "default",
            "&:hover": toggle ? { opacity: 0.75 } : {},
        }}
    >
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: "0.5px",
            textTransform: "uppercase", flex: 1, minWidth: 0 }}>
            {label}
        </Typography>
        {count != null && (
            <Typography sx={{ fontSize: 11, color: "#cbd5e1", fontWeight: 600 }}>{count}</Typography>
        )}
        {toggle && (
            // 아이콘을 갈아치우지 않고 <b>회전</b>시킨다 — 교체는 순간이동처럼 보여
            // 아래 목록의 열림 애니메이션과 따로 놀았다.
            <ExpandLessRoundedIcon sx={{
                fontSize: 14, color: C.textMuted,
                transition: "transform 160ms ease",
                transform: expanded ? "none" : "rotate(180deg)",
            }} />
        )}
    </Box>
);

/**
 * 폴더 한 줄 — 메일 사이드바의 하위 폴더 행과 같은 모양(↳ + 폴더 아이콘 + 이름 + 건수).
 *
 * <p><b>중첩은 없다.</b> 폴더 안에 폴더를 만들지 않기로 했으므로 depth 개념 자체를 두지 않았다
 * (메일은 재귀 렌더지만 여기는 평면 목록이다). 나중에 중첩이 필요해지면 그건 "폴더가 아니라
 * 채널이 나뉘어야 한다" 는 신호로 본다.
 */
const FolderRow = ({ channelId, id, name, count, color, icon, tooltip, ctx }) => {
    const { selectedId, selectedFolder, onSelectFolder } = ctx;
    /*
     * 활성 표시는 <b>지금 보고 있는 채널 안에서만</b>.
     *
     * 채널 조건 없이 selectedFolder 만 비교하면, 펼쳐 둔 모든 채널의 '전체 폴더' 줄이 한꺼번에
     * 파랗게 된다 — ALL(null)·UNFILED 는 폴더 id 와 달리 채널마다 <b>같은 값</b>이기 때문이다.
     */
    const active = channelId === selectedId && selectedFolder === id;
    const row = (
        <Box
            onClick={(e) => { e.stopPropagation(); onSelectFolder(channelId, id); }}
            sx={{
                display: "flex", alignItems: "center", gap: 0.5,
                pl: 3.2, pr: 1, py: 0.45, mx: 0.5, borderRadius: 1.2, cursor: "pointer",
                bgcolor: active ? C.primaryBg : "transparent",
                color: active ? C.primaryText : "#475569",
                fontWeight: active ? 700 : 500,
                "&:hover": { bgcolor: active ? C.primaryBg : "#f1f5f9" },
                transition: "background-color 0.12s",
            }}
        >
            <SubdirectoryArrowRightRoundedIcon sx={{ fontSize: 13, color: "#cbd5e1", flexShrink: 0 }} />
            {icon ?? <FolderRoundedIcon sx={{ fontSize: 15, color, flexShrink: 0 }} />}
            <Typography noWrap sx={{ fontSize: 12.5, flex: 1, minWidth: 0, color: "inherit", fontWeight: "inherit" }}>
                {name}
            </Typography>
            {count > 0 && (
                <Typography sx={{ fontSize: 11, color: active ? "inherit" : "#94a3b8", fontWeight: 600 }}>
                    {count}
                </Typography>
            )}
        </Box>
    );
    return tooltip ? <Tooltip title={tooltip} placement="right">{row}</Tooltip> : row;
};

/**
 * 채널 행 아래에 붙는 폴더 목록 — <b>선택과 무관하게</b> 채널마다 따로 열고 닫는다.
 *
 * <h4>예전엔 선택된 채널만 폈다</h4>
 * <p>그때는 폴더 목록을 그 채널을 고른 뒤에야 따로 조회했기 때문에, 선택되지 않은 채널의 폴더는
 * 애초에 손에 없었다. 이제는 채널 목록이 <b>모든 채널의 폴더</b>를 함께 실어 오므로
 * (<code>ChannelPage.foldersByChannel</code>) 그 제약이 사라졌다.
 *
 * <p>바뀐 규칙은 둘이다:
 * <ul>
 *   <li>다른 채널을 열어도 이미 펴 둔 목록은 <b>닫히지 않는다</b>.</li>
 *   <li>채널을 고르지 않고 <b>⌄ 만 눌러</b> 다른 프로젝트에 뭐가 있는지 볼 수 있다.</li>
 * </ul>
 *
 * <p>기본값은 접힘이다({@code expandedFolders} 가 «편 것» 을 담는다) — 전 채널이 처음부터
 * 펼쳐져 있으면 20개 채널에 폴더 서너 개씩만 붙어도 사이드바가 100줄이 된다.
 * 고른 채널만 자동으로 펴 준다.
 */
const FolderTree = ({ channel, ctx }) => {
    const { foldersByChannel, expandedFolders } = ctx;

    const data = foldersByChannel[channel.id];
    const folders = data?.folders ?? [];
    const unfiledFeedCount = data?.unfiledFeedCount ?? 0;

    // 폴더가 없는 채널은 접을 것도 없다 — 껍데기를 두면 채널 수만큼 빈 래퍼가 생긴다.
    if (folders.length === 0) return null;

    const open = expandedFolders.has(channel.id);
    const totalCount = folders.reduce((n, f) => n + (f.feedCount || 0), 0) + unfiledFeedCount;

    return (
        <CollapseRows open={open}>
            <Box sx={{ pb: 0.5 }}>
                {/*
                  * '전체 폴더' — 이 채널의 피드 전부. 초록 폴더로 다른 폴더(앰버)와 구분한다.
                  *
                  * 배경 강조는 <b>이 목록에서만</b> 준다(채널 행은 굵은 글씨로만 현재 채널을 표시) —
                  * 그래야 파랗게 채워진 줄이 언제나 하나뿐이라 이중 활성으로 보이지 않는다.
                  */}
                <FolderRow ctx={ctx} channelId={channel.id} id={ALL} name="전체 폴더"
                    count={totalCount} color="#16a34a" tooltip="이 채널의 모든 폴더 + 미분류" />
                {folders.map(f => (
                    <FolderRow ctx={ctx} channelId={channel.id} key={f.id} id={f.id} name={f.name}
                        count={f.feedCount} color="#f59e0b"
                        tooltip={f.memberCount === 0
                            ? "참가자 없음 — PM·관리자만 열람"
                            : `참가자 ${f.memberCount}명${f.description ? ` · ${f.description}` : ""}`} />
                ))}
                {/* 미분류는 실제로 있을 때만 — 폴더를 쓰는 채널에도 0건이면 군더더기다. */}
                {unfiledFeedCount > 0 && (
                    <FolderRow ctx={ctx} channelId={channel.id} id={UNFILED} name="미분류"
                        count={unfiledFeedCount} color="#cbd5e1" tooltip="폴더를 지정하지 않은 항목" />
                )}
            </Box>
        </CollapseRows>
    );
};

/**
 * @param indented 채널 행을 한 단계 들여쓴다. <b>모든 구간이 켠다</b> — 즐겨찾기·프로젝트·기타가
 *   전부 헤더 아래에 놓이므로, 한 곳만 왼쪽 끝에 붙어 있으면 그 구간만 계층이 한 단 얕아 보인다.
 *   (예전엔 프로젝트만 켰다. 거기만 «구간 헤더» 가 한 겹 더 있었기 때문인데, 정작 눈에는
 *   구간 깊이가 아니라 채널 이름의 왼쪽 기준선이 들쭉날쭉한 것으로 읽혔다.)
 */
const ChannelItem = ({ channel, dimmed = false, indented = false, ctx }) => {
    // selectedFolder 는 쓰지 않는다 — 폴더 범위 강조는 폴더 목록(FolderRow)이 전담한다.
    const {
        selectedId, hasNewMap, favorites, foldersByChannel, expandedFolders,
        onSelect, onManageFolders, onEditChannel, toggleFavorite, toggleFolders,
    } = ctx;
    const isCurrent = channel.id === selectedId;
    const loadedFolders = foldersByChannel[channel.id]?.folders ?? [];
    /** 채널 ADMIN·PM·전체 관리자 — 채널 수정·폴더 관리를 함께 가리는 하나의 판정. */
    const canManage = channel.canManage === true;
    /**
     * 배경 강조는 <b>화면에서 언제나 한 줄에만</b> 있어야 한다(이중 활성 방지).
     *
     * <p>폴더가 있는 채널이면 범위를 고르는 줄이 폴더 목록(전체 폴더 / 각 폴더 / 미분류)이므로
     * 강조를 그쪽에 양보하고, 채널 행은 <b>굵은 글씨</b>로만 현재 채널임을 표시한다.
     * 폴더가 없는 채널은 채널 행 자체가 유일한 선택지라 그대로 배경을 갖는다.
     *
     * <p>단, 폴더 목록을 <b>접어 둔</b> 동안은 채널 행이 다시 유일한 줄이 되므로 배경을 되찾는다.
     * 안 그러면 접은 순간 화면에서 «지금 보고 있는 채널» 표시가 통째로 사라진다.
     */
    const foldersOpen = expandedFolders.has(channel.id);
    const selected = isCurrent && (loadedFolders.length === 0 || !foldersOpen);
    const hasNew = hasNewMap?.[channel.id] ?? false;
    const isFav = favorites.has(channel.id);
    /**
     * 폴더 개수 — 배지·토글에 쓴다.
     *
     * <p>두 출처를 쓴다: 목록에 실려 온 폴더 목록의 길이와, 같은 응답의 {@code folderCount}.
     * 전자를 우선하는 이유는 폴더를 만들거나 지운 <b>직후</b>에 상세 조회로 갱신되는 쪽이라
     * 항상 더 최신이기 때문이다.
     */
    const folderCount = loadedFolders.length || (channel.folderCount ?? 0);
    const [hovered, setHovered] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);

    return (
        <Box
            // 채널 행 클릭 = 그 채널의 '전체' 보기. 폴더 선택 초기화는 ChannelPage 의 채널 전환
            // effect 가 맡는다 — 여기서 같이 부르면 아직 이전 채널이 selectedId 인 시점이라
            // 엉뚱한 채널에 대고 폴더를 바꾸게 된다.
            onClick={() => onSelect(channel.id)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{
                // 행마다 있던 borderBottom 을 없앴다 — 항목이 20개면 회색 줄도 20개가 되어
                // 사이드바가 표처럼 보인다. 구분은 여백(py)과 hover 배경이 이미 충분히 한다.
                //
                // 들여쓰기는 <b>항목에만</b> 준다(헤더는 안 준다) — 그래야 "프로젝트 > 진행중 > 채널"
                // 순으로 왼쪽 기준선이 단계적으로 밀려 계층이 눈으로 읽힌다.
                //
                // 선택 표시는 메일 사이드바와 같은 방식 — 좌측 3px 띠 대신 <b>둥근 배경</b>.
                // 띠는 pane 가장자리에 붙어 있어야 하는 탓에 행을 라운드로 만들 수 없고,
                // 같은 화면 안의 메일 사이드바와 선택 표현이 달라 보였다.
                pl: indented ? 3 : 1.2, pr: 1, py: 0.9,
                mx: 0.5, borderRadius: 1.2, cursor: "pointer",
                bgcolor: selected ? C.primaryBg : "transparent",
                "&:hover": { bgcolor: selected ? C.primaryBg : "#f1f5f9" },
                transition: "background-color 0.12s",
                opacity: dimmed ? 0.5 : 1,
            }}
        >
            <Stack direction="row" alignItems="center" spacing={0.6} mb={0.2}>
                {/*
                  * (제거됨) 별 아이콘 — 즐겨찾기는 ⋮ 메뉴에서 등록·해제한다.
                  *
                  * 채널명 폭이 250px 사이드바에서 이미 빠듯한데 별·개수·⋮·토글 네 개가 붙자
                  * 이름이 "2026 TEL Family ..." 로 잘렸다. 즐겨찾기는 <b>가끔</b> 쓰는 동작이라
                  * 상시 자리를 차지할 이유가 없고, 즐겨찾기된 채널은 위 '즐겨찾기' 섹션에 모여
                  * 보이므로 행에 표식이 없어도 알 수 있다.
                  */}
                <Box flex={1} minWidth={0}>
                    <Typography sx={{
                        fontSize: 13,
                        // 배경은 '전체' 일 때만이지만, 이름 강조는 그 채널을 보고 있는 동안 유지한다.
                        fontWeight: isCurrent ? 700 : 600,
                        color: isCurrent ? C.primaryText : "#1e293b", lineHeight: 1.3,
                    }} noWrap>
                        {channel.name}
                    </Typography>
                </Box>
                {hasNew && !isCurrent && (
                    <Box sx={{
                        width: 8, height: 8, borderRadius: "50%",
                        bgcolor: "#ef4444", flexShrink: 0,
                    }} />
                )}
                {/* 폴더 개수 — 내가 볼 수 있는 폴더 수(서버가 가시성 적용해 내려준다). */}
                {folderCount > 0 && (
                    <Tooltip title={`폴더 ${folderCount}개`}>
                        <Stack direction="row" alignItems="center" spacing={0.2} sx={{ flexShrink: 0 }}>
                            <FolderRoundedIcon sx={{ fontSize: 12, color: "#cbd5e1" }} />
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted }}>
                                {folderCount}
                            </Typography>
                        </Stack>
                    </Tooltip>
                )}
                {/* ⋮ — 채널별 부가 동작(폴더 관리). hover 또는 현재 채널일 때만 보인다.
                    자리는 늘 잡아 둬서 오른쪽 열(토글)이 행마다 어긋나지 않게 한다. */}
                <Box sx={{ width: 18, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                    {(hovered || isCurrent) && (
                        <Box
                            onClick={e => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}
                            sx={{ display: "flex", alignItems: "center",
                                borderRadius: 1, "&:hover": { bgcolor: "rgba(15,23,42,0.06)" } }}
                        >
                            <MoreVertRoundedIcon sx={{ fontSize: 16, color: C.textMuted }} />
                        </Box>
                    )}
                </Box>
                {/*
                  * 폴더 펼치기 토글 — 행 맨 오른쪽. 폴더가 없는 채널도 자리는 남겨 열을 맞춘다.
                  *
                  * <b>현재 채널이 아니어도 보인다.</b> 이 화살표의 용도가 «채널을 열지 않고
                  * 그 프로젝트에 뭐가 있는지 보는 것» 이라, 고른 채널에만 있으면 먼저 들어가야만
                  * 볼 수 있어 쓸모가 없다. stopPropagation 이 있어 눌러도 채널 이동은 안 한다.
                  */}
                <Box sx={{ width: 16, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                    {folderCount > 0 && (
                        <Tooltip title={foldersOpen ? "폴더 접기" : `폴더 ${folderCount}개 펼치기`}>
                            <IconButton size="small"
                                onClick={e => { e.stopPropagation(); toggleFolders(channel.id); }}
                                sx={{ p: 0 }}>
                                <ExpandLessRoundedIcon sx={{
                                    fontSize: 16, color: C.textMuted,
                                    transition: "transform 160ms ease",
                                    transform: foldersOpen ? "none" : "rotate(180deg)",
                                }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    onClick={e => e.stopPropagation()}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                    slotProps={{ paper: { sx: { borderRadius: 1.5, minWidth: 150 } } }}>
                    {/*
                      * 관리자 전용 항목(채널 수정 · 폴더 추가·관리) — <b>권한이 없으면 아예 안 띄운다.</b>
                      *
                      * 둘은 서버에서 같은 판정({@code assertChannelAdmin})으로 막히는데, 화면에서는
                      * 처리가 갈려 있었다: «채널 수정» 은 숨기고 «폴더 추가·관리» 는 띄운 뒤 눌렀을 때
                      * "PM 만 가능합니다" 를 알렸다. 같은 메뉴 안에서 규칙이 둘이면 사용자는 무엇이
                      * 눌리는 항목인지 배우질 못한다 — 눌러 봐야 아는 항목이 섞여 있기 때문이다.
                      * 판정이 하나이므로 표시도 하나로 맞춘다.
                      *
                      * 채널 수정만 보관 채널에서 추가로 빠진다 — 보관되면 모든 쓰기가 막히지만
                      * ({@code assertWritable}) 폴더 관리 다이얼로그는 열람용으로도 쓸모가 있다.
                      * 본문 헤더가 보관 채널에서 «채널 수정» 을 «보관 해제» 로 바꿔 다는 것과 같은 이유다.
                      */}
                    {canManage && !channel.archived && (
                        <MenuItem
                            onClick={() => { setMenuAnchor(null); onEditChannel(channel.id); }}
                            sx={{ fontSize: 13, gap: 1 }}>
                            <EditRoundedIcon sx={{ fontSize: 16 }} /> 채널 수정
                        </MenuItem>
                    )}
                    {canManage && (
                        <MenuItem
                            onClick={() => { setMenuAnchor(null); onManageFolders(channel.id); }}
                            sx={{ fontSize: 13, gap: 1 }}>
                            <CreateNewFolderRoundedIcon sx={{ fontSize: 16 }} /> 폴더 추가·관리
                        </MenuItem>
                    )}
                    <MenuItem
                        onClick={e => { setMenuAnchor(null); toggleFavorite(e, channel.id); }}
                        sx={{ fontSize: 13, gap: 1 }}>
                        {isFav
                            ? <><StarBorderRoundedIcon sx={{ fontSize: 16 }} /> 즐겨찾기 해제</>
                            : <><StarRoundedIcon sx={{ fontSize: 16, color: "#f59e0b" }} /> 즐겨찾기</>}
                    </MenuItem>
                </Menu>
            </Stack>
            {channel.type === "PROJECT" && (() => {
                /**
                 * 채널명 아래 한 줄 표시.
                 * - 우선 D-N (마감일 기준, 마감일 없으면 시작일까지)
                 * - 옆에 전체 일정: MM.DD ~ MM.DD
                 * - 시작일·마감일 모두 없으면 "일정 미지정" 만
                 */
                // 계산·표기는 dday.js 한 곳에서 — 본문 헤더와 다른 숫자가 나오던 자리.
                const start = toLocalDate(channel.startDate);
                const deadline = toLocalDate(channel.deadlineDate);
                const fmt = fmtDate;

                if (!start && !deadline) {
                    return (
                        <Typography sx={{ fontSize: 11, color: "#94a3b8" }} noWrap>
                            일정 미지정
                        </Typography>
                    );
                }

                const range = start && deadline
                    ? `${fmt(start)} ~ ${fmt(deadline)}`
                    : start ? `${fmt(start)} ~` : `~ ${fmt(deadline)}`;

                let dday = "";
                if (deadline && !channel.archived) {
                    dday = ddayLabel(deadline) || "";
                } else if (start && !channel.archived) {
                    const diff = daysUntil(start);
                    if (diff > 0) dday = `시작 D-${diff}`;
                }

                return (
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }} noWrap>
                        {dday && (<><Box component="span" sx={{ fontWeight: 700 }}>{dday}</Box>{" · "}</>)}
                        {range}
                    </Typography>
                );
            })()}
        </Box>
    );
};

/**
 * 프로젝트 구간 색 — 펼침의 구간 헤더(색 폴더)와 접힘 레일이 <b>같은 색</b>이어야 한다.
 * 두 곳에 하드코딩돼 있으면 한쪽만 고쳐져 폈다 접을 때 채널 색이 바뀐다.
 */
const PHASE_COLOR = { ongoing: "#34d399", upcoming: "#a78bfa", undated: "#94a3b8", finished: "#f87171" };

/** 기타 채널 — 펼침에 구간 색이 없다. '일정 미지정'의 회색과 겹치지 않게 한 단계 진하게 둔다. */
const OTHER_COLOR = "#64748b";

/**
 * 접힘(48px) 레일 — 채널 하나당 아이콘 한 칸.
 *
 * <p>남기는 것: <b>구간 색 · 안읽음 점 · Tooltip(채널명)</b>. 이 셋이 없으면 아이콘이 전부 같은
 * 회색 폴더가 되어 20개 중 무엇을 누르는지 알 수 없다. 특히 Tooltip 은 필수다 —
 * 진행중 채널이 여럿이면 색까지 같아 아이콘만으로는 구분이 <b>원리적으로</b> 불가능하다.
 *
 * <p>숨기는 것: 채널명 · D-day · 폴더 개수 · ⋮ 메뉴. 폴더 트리도 접힘에서는 그리지 않는다 —
 * 하위 폴더까지 48px 에 세우면 어느 줄이 채널이고 어느 줄이 폴더인지 구분이 사라진다.
 * 폴더는 채널을 고른 뒤(=펼쳐진 뒤) 고르는 것이라 접힘에서 잃어도 순서가 어긋나지 않는다.
 *
 * <p>구간 사이는 얇은 선으로만 끊는다. 펼침의 «즐겨찾기 / 프로젝트 / 기타» 경계를 아주 잃으면
 * 20개 아이콘이 한 덩어리가 된다.
 */
const ChannelRail = ({ items, selectedId, onPick }) => (
    <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pt: 0.5, pb: 1 }}>
        {items.map((it, i) => (
            <React.Fragment key={it.id}>
                {/* 앞 항목과 그룹이 달라지는 자리에만 선 — 첫 줄 위에는 두지 않는다. */}
                {i > 0 && it.group !== items[i - 1].group && (
                    <Divider sx={{ mx: 1.2, my: 0.6, borderColor: C.border }} />
                )}
                <Tooltip title={it.tooltip} placement="right">
                    <Box
                        onClick={() => onPick(it.id)}
                        sx={{
                            position: "relative",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            height: 34, mx: 0.5, borderRadius: 1.2, cursor: "pointer",
                            bgcolor: it.id === selectedId ? C.primaryBg : "transparent",
                            "&:hover": { bgcolor: it.id === selectedId ? C.primaryBg : "#f1f5f9" },
                            transition: "background-color 0.12s",
                            opacity: it.dimmed ? 0.5 : 1,
                        }}
                    >
                        {it.fav
                            ? <StarRoundedIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
                            : <FolderRoundedIcon sx={{ fontSize: 18, color: it.color }} />}
                        {/* 안읽음 — 펼침과 같은 규칙(보고 있는 채널에는 띄우지 않는다).
                            흰 테두리를 둘러 아이콘 위에 겹쳐도 점으로 읽히게 한다. */}
                        {it.hasNew && it.id !== selectedId && (
                            <Box sx={{
                                position: "absolute", top: 4, right: 7,
                                width: 8, height: 8, borderRadius: "50%",
                                bgcolor: "#ef4444", border: "1.5px solid #fff",
                            }} />
                        )}
                    </Box>
                </Tooltip>
            </React.Fragment>
        ))}
    </Box>
);

function ChannelSidebar({
    channels, selectedId, onSelect, onCreateOpen, hasNewMap, onManageFolders,
    /** (channelId) — ⋮ → 채널 수정. 그 채널로 이동한 뒤 수정 다이얼로그를 연다. */
    onEditChannel,
    /** (channelId, nextValue) — 즐겨찾기 등록·해제. 서버 반영은 ChannelPage 가 맡는다. */
    onToggleFavorite,
    /** {@code channelId → { folders, unfiledFeedCount, canManage }} — <b>모든</b> 채널 몫. */
    foldersByChannel = {},
    selectedFolder, onSelectFolder,
}) {
    const history = useHistory();
    const [showOngoingProj, setShowOngoingProj] = useState(true);
    const [showUpcomingProj, setShowUpcomingProj] = useState(true);
    const [showUndatedProj, setShowUndatedProj] = useState(true);
    const [showArchivedProj, setShowArchivedProj] = useState(false);
    const [showFavorites, setShowFavorites] = useState(true);
    /**
     * 폴더 목록을 <b>펼친</b> 채널 id 집합.
     *
     * <p>예전엔 반대로 «접은 것» 을 담았다. 폴더 목록이 선택된 채널에만 붙던 시절엔 화면에 트리가
     * 하나뿐이라 기본값(빈 집합 = 전부 펼침)이 자연스러웠기 때문이다. 이제는 모든 채널이 자기
     * 트리를 가질 수 있어 그 규약이면 <b>첫 화면부터 전부 펼쳐진다</b> — 채널 20개에 폴더가
     * 서너 개씩만 붙어도 100줄짜리 사이드바가 된다. 그래서 «편 것» 을 담는 쪽으로 뒤집었다.
     *
     * <p>여기 들어오는 경로는 둘이다: 채널을 고르면 자동으로(아래 effect), 또는 사용자가 ⌄ 를
     * 직접 눌러서. 나가는 경로는 ⌄ 를 다시 누르는 것 <b>하나뿐</b>이다 — 다른 채널로 옮긴다고
     * 해서 이미 펴 둔 목록을 닫지 않는다.
     */
    const [expandedFolders, setExpandedFolders] = useState(new Set());
    const toggleFolders = (channelId) => setExpandedFolders(prev => {
        const next = new Set(prev);
        if (next.has(channelId)) next.delete(channelId); else next.add(channelId);
        return next;
    });

    /**
     * 고른 채널의 폴더 목록은 <b>자동으로 펼친다</b> — 채널을 여는 목적이 그 안을 보는 것이라
     * 접힌 채로 두면 한 번 더 눌러야 한다.
     *
     * <p>클릭이 아니라 {@code selectedId} 변화를 본다. 알림 딥링크나 새로고침처럼 <b>클릭 없이</b>
     * 채널이 정해지는 경로가 있어서, 클릭 핸들러에 넣으면 그때는 접힌 채로 뜬다.
     */
    useEffect(() => {
        if (selectedId == null) return;
        setExpandedFolders(prev => prev.has(selectedId) ? prev : new Set(prev).add(selectedId));
    }, [selectedId]);

    /**
     * 즐겨찾기 — <b>서버가 채널마다 {@code favorite} 를 실어 준다</b>({@code ChannelFavorite}).
     *
     * <p>여기서 상태로 들지 않는다. 들고 있으면 «화면이 아는 값» 과 «서버가 아는 값» 이 갈려,
     * 다른 기기에서 바꾸고 와서 목록을 새로 받아도 화면이 옛 값을 고집한다. 목록에서 그대로
     * 파생시키면 그 어긋남이 원리적으로 생기지 않는다.
     *
     * <p>예전엔 localStorage 였다 — 같은 사람이 PC 와 노트북에서 다른 즐겨찾기를 갖고
     * 브라우저 데이터를 지우면 날아갔다. 그래서 «기억» 을 걷어낼 때 이것만 서버로 옮겼다.
     */
    const favorites = useMemo(
        () => new Set((channels || []).filter(c => c.favorite).map(c => c.id)),
        [channels]);

    const toggleFavorite = (e, channelId) => {
        e.stopPropagation();
        onToggleFavorite(channelId, !favorites.has(channelId));
    };

    const projectsAll     = channels.filter(c => c.type === "PROJECT" && !favorites.has(c.id));
    const projectOngoing  = projectsAll.filter(c => projectPhase(c) === "ongoing");
    const projectUpcoming = projectsAll.filter(c => projectPhase(c) === "upcoming");
    const projectUndated  = projectsAll.filter(c => projectPhase(c) === "undated");
    const projectFinished = projectsAll.filter(c => projectPhase(c) === "finished");
    /**
     * 기타 — 프로젝트가 아닌 모든 채널.
     *
     * <p>{@code type === "OTHER"} 로 좁히지 않는다. 폐지된 공지·팀 채널은 서버 러너가 OTHER 로
     * 전환하지만, 러너가 아직 돌지 않은 DB 에서는 여전히 NOTICE/TEAM 인 채로 온다. 그때
     * 등호 비교였다면 그 채널들이 <b>어느 섹션에도 안 잡혀 화면에서 사라진다</b>(데이터는 살아 있는데
     * 열 방법이 없는 상태). 부정 조건으로 두면 항상 어딘가에는 보인다.
     */
    const otherChannels   = channels.filter(c => c.type !== "PROJECT" && !c.archived && !favorites.has(c.id));

    const favoriteChannels = channels.filter(c => favorites.has(c.id));

    /**
     * 접힘 레일용 평면 목록 — <b>펼침과 같은 순서</b>로 만든다.
     *
     * <p>접었다 폈을 때 채널이 다른 자리에 있으면 "같은 목록" 으로 느껴지지 않는다. 그래서
     * 즐겨찾기 → 프로젝트(진행중·예정·미지정·종료) → 기타 순서와 각 구간의 색·흐림을 그대로 옮긴다.
     *
     * <p>구간 <b>헤더 접기</b>는 반영하지 않는다(닫아 둔 구간의 채널도 레일에는 나온다).
     * 48px 에는 헤더가 없어 "왜 안 보이는지" 를 설명할 자리가 없기 때문이다 — 안 보이면 사라진 걸로 읽힌다.
     */
    const railItems = useMemo(() => {
        const at = (c, group, color, dimmed) => ({
            id: c.id, group, color, dimmed,
            fav: group === "fav",
            hasNew: hasNewMap?.[c.id] ?? false,
            tooltip: c.name,
        });
        return [
            ...favoriteChannels.map(c => at(c, "fav", "#f59e0b", false)),
            ...projectOngoing.map(c => at(c, "proj", PHASE_COLOR.ongoing, false)),
            ...projectUpcoming.map(c => at(c, "proj", PHASE_COLOR.upcoming, false)),
            ...projectUndated.map(c => at(c, "proj", PHASE_COLOR.undated, true)),
            ...projectFinished.map(c => at(c, "proj", PHASE_COLOR.finished, true)),
            ...otherChannels.map(c => at(c, "other", OTHER_COLOR, false)),
        ];
    // 위 배열들은 매 렌더 새로 만들어지는 파생값이라 원본(channels/favorites)을 의존성으로 둔다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channels, favorites, hasNewMap]);

    /**
     * 하위 컴포넌트에 넘길 값 묶음.
     *
     * <p>{@code ChannelItem}·{@code FolderTree} 는 <b>모듈 최상위</b>에 있어야 접기 애니메이션과
     * ⋮ 메뉴 state 가 유지된다(파일 상단 주석 참고). 그래서 클로저로 값을 가져다 쓸 수 없고
     * 이렇게 한 덩어리로 전달한다 — 인자를 하나씩 늘리면 호출부 세 곳을 매번 같이 고쳐야 한다.
     *
     * <p>{@code onSelect} 는 부모 것을 <b>그대로</b> 넘긴다. 예전엔 여기서 한 겹 감싸
     * «이전 채널 트리를 200ms 접고 이동» 했는데(그때는 새 채널 폴더가 왕복 뒤에야 와서 그 빈 시간을
     * 덮어야 했다), 이제 폴더가 채널 목록에 실려 오므로 기다릴 것이 없다. 고른 채널을 펼치는 일은
     * 위 {@code selectedId} effect 가 맡는다 — 클릭 말고 딥링크로 들어오는 경로도 있어서다.
     */
    const ctx = useMemo(() => ({
        selectedId, selectedFolder, hasNewMap, favorites, foldersByChannel, expandedFolders,
        onSelect, onSelectFolder, onManageFolders, onEditChannel, toggleFavorite, toggleFolders,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [selectedId, selectedFolder, hasNewMap, favorites, foldersByChannel, expandedFolders,
        onSelect, onSelectFolder, onManageFolders, onEditChannel]);

    return (
        <CollapsibleSidebar
            storageKey="channel.sidebar"
            /* 접힘 상태를 저장하지 않는다 — 채널 화면의 «기억» 을 전부 걷어내기로 한 결정에 맞춘다.
               storageKey 는 자동 접기 예고(sessionStorage)에 여전히 쓰이므로 남긴다. */
            persist={false}
            width={260}
            expandTitle="채널 목록 펼치기"
            collapseTitle="채널 목록 접기"
            renderCollapsed={() => (
                <ChannelRail
                    items={railItems}
                    selectedId={selectedId}
                    // 폴더 선택 초기화는 ChannelPage 의 채널 전환 effect 가 맡는다(펼침 목록과 같은 규칙).
                    onPick={(id) => onSelect(id)}
                />
            )}
            sx={{
                // 두 pane(목록 | 내용)을 가르는 선 하나만 남긴다 — 여기서 지우면 목록과 본문이 붙어 보인다.
                borderRight: `1px solid ${C.border}`,
                bgcolor: C.surface,
            }}
        >
            {/* 헤더 — 제목 아래 Divider 를 뺐다. 바로 아래 섹션 헤더가 이미 경계를 만들어 선이 겹쳤다.
                pt 를 줄인 건 위에 토글 줄이 하나 생겼기 때문이다(원래 여백을 두면 제목이 내려앉는다). */}
            <Box sx={{ px: 2, pt: 0.5, pb: 0.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ ...ds.typography.pageTitle, fontSize: 17 }}>채널</Typography>
                    <ActionButton variant="add"
                        startIcon={<AddRoundedIcon sx={{ fontSize: 15 }} />}
                        onClick={onCreateOpen}
                        sx={{ fontSize: 13, py: 0.5 }}
                        label="추가" />
                </Stack>
            </Box>

            {/*
              * overflowX 를 <b>명시적으로</b> 끈다 — 접힘 레일(ChannelRail)은 원래 그렇게 돼 있었는데
              * 여기만 빠져 있었다.
              *
              * <p>CSS 규칙 하나 때문이다: {@code overflow-y} 만 auto 로 주면 {@code overflow-x} 의
              * {@code visible} 이 <b>auto 로 승격</b>된다(visible 은 다른 값과 짝을 이룰 수 없다).
              * 즉 이 상자는 «세로만 스크롤» 이 아니라 «양쪽 다 스크롤» 이었다.
              *
              * <p>그래서 폴더 트리를 채널마다 펼 수 있게 만들자 <b>가로 스크롤바가 생겼다</b>.
              * 폭이 줄어든 게 아니라, 목록이 길어져 <b>세로</b> 스크롤바가 생기면서 그게 250px 중
              * 15px 를 먹었고, 원래 그 15px 여유로 겨우 들어가 있던 줄이 넘친 것이다.
              * 사이드바는 가로로 스크롤할 이유가 없고 글자는 이미 전부 말줄임(noWrap)이라 잘라 낸다.
              */}
            <Box sx={{
                flex: 1, overflowY: "auto", overflowX: "hidden",
                /*
                 * 스크롤바를 <b>가늘게, 평소엔 안 보이게</b>.
                 *
                 * 채널이 스무 개쯤 되고 폴더까지 펼치면 내용이 250px 칸보다 길어지는 건 피할 수
                 * 없다(그때는 스크롤이 <i>있어야</i> 나머지를 볼 수 있다). 문제는 스크롤 «기능» 이
                 * 아니라 회색 막대가 상시 붙어 폭을 15px 먹고 목록을 표처럼 보이게 만드는 것이었다.
                 *
                 * 그래서 두께를 6px 로 줄이고 손잡이는 <b>사이드바에 마우스를 올렸을 때만</b>
                 * 칠한다. 스크롤할 수 있다는 단서는 남기면서 평소 화면에서는 사라진다.
                 * (완전히 width:0 으로 지우면 «아래 더 있다» 를 알 방법이 없어진다.)
                 */
                scrollbarWidth: "thin",
                scrollbarColor: "transparent transparent",
                "&:hover": { scrollbarColor: "#cbd5e1 transparent" },
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "transparent", borderRadius: 3,
                    transition: "background-color 160ms ease",
                },
                "&:hover::-webkit-scrollbar-thumb": { backgroundColor: "#cbd5e1" },
            }}>
                {/* 즐겨찾기 섹션 */}
                {favoriteChannels.length > 0 && (
                    <>
                        {/* ★ 를 달지 않는다 — 아이콘 폭만큼 이 라벨만 오른쪽으로 밀려
                            «프로젝트»·«기타» 와 시작점이 어긋난다. 글자가 이미 «즐겨찾기» 라
                            아이콘이 설명을 보태지도 않고, 노란 별은 ⋮ 메뉴와 접힘 레일에 남아 있다. */}
                        <SectionHeader
                            label="즐겨찾기"
                            count={favoriteChannels.length}
                            toggle={() => setShowFavorites(v => !v)}
                            expanded={showFavorites}
                        />
                        <CollapseRows open={showFavorites}>
                            {favoriteChannels.map(c => (
                                <React.Fragment key={c.id}>
                                    <ChannelItem ctx={ctx} channel={c} indented />
                                    <FolderTree ctx={ctx} channel={c} />
                                </React.Fragment>
                            ))}
                        </CollapseRows>
                    </>
                )}

                {projectsAll.length > 0 && favoriteChannels.length > 0 && <TypeBoundary />}

                {projectsAll.length > 0 && (() => {
                    /**
                     * 프로젝트 — 단일 상위 섹션 + 4개 세부 토글 (진행중 / 예정 / 일정 미지정(대기) / 종료).
                     * 기본 활성: 진행중·예정·일정 미지정 = 열림 / 종료 = 닫힘.
                     *
                     * <h4>표시 규칙</h4>
                     * <ul>
                     *   <li>{@code alwaysShowHeader} — list 가 0건이어도 헤더 표시 (종료된 프로젝트 항상 노출용)</li>
                     *   <li>토글이 닫혀도 현재 선택된 채널이 list 안에 있으면 그 채널은 강제로 노출</li>
                     * </ul>
                     */
                    const subSections = [
                        // dot 색은 PHASE_COLOR 한 곳에서 — 접힘 레일이 같은 색을 써야 한다.
                        { key: "ongoing",  label: "진행중",          dot: PHASE_COLOR.ongoing,  list: projectOngoing,  open: showOngoingProj,  setOpen: setShowOngoingProj,  dimmed: false, alwaysShowHeader: false },
                        { key: "upcoming", label: "예정",            dot: PHASE_COLOR.upcoming, list: projectUpcoming, open: showUpcomingProj, setOpen: setShowUpcomingProj, dimmed: false, alwaysShowHeader: false },
                        { key: "undated",  label: "일정 미지정(대기)", dot: PHASE_COLOR.undated,  list: projectUndated,  open: showUndatedProj,  setOpen: setShowUndatedProj,  dimmed: true,  alwaysShowHeader: false },
                        { key: "finished", label: "종료",            dot: PHASE_COLOR.finished, list: projectFinished, open: showArchivedProj, setOpen: setShowArchivedProj, dimmed: true,  alwaysShowHeader: true },
                    ];
                    const visibleSubs = subSections.filter(s => s.alwaysShowHeader || s.list.length > 0);
                    if (visibleSubs.length === 0) return null;
                    return (
                        <>
                            <SectionHeader label="프로젝트" count={projectsAll.length} />
                            {visibleSubs.map((sub, subIdx) => {
                                /*
                                 * 접었을 때도 «지금 보고 있는 채널» 한 줄은 남긴다.
                                 *
                                 * 이걸 «접힘용 목록» 으로 따로 그리고, 펼침용 전체 목록과 <b>둘 다</b>
                                 * CollapseRows 로 감싼다. 하나가 줄어드는 동안 다른 하나가 자라므로
                                 * 전이가 끊기지 않는다 — 접힘용을 그냥 조건부로 render/unmount 하면
                                 * 토글하는 순간 그 줄이 사라졌다가 애니메이션이 끝나야 다시 나타난다.
                                 */
                                const pinned = sub.list.filter(c => c.id === selectedId);
                                return (
                                    <Box key={sub.key}>
                                        {/*
                                          * 구간 사이 구분선 — 진행중/예정/일정 미지정/종료는 <b>서로 다른 상태</b>라
                                          * 붙여 놓으면 한 덩어리로 읽힌다. 여백 + 옅은 선으로 "여기서 상태가 바뀐다" 를
                                          * 눈에 끊어 준다. 첫 구간 위에는 이미 '프로젝트' 헤더가 있어 선을 넣지 않는다.
                                          *
                                          * 선만 안쪽으로 들이고(mx) 구간 자체는 사이드바 폭을 그대로 쓴다 —
                                          * 구간에 여백을 주면 선택된 채널의 좌측 accent bar 가 pane 가장자리에서 떨어진다.
                                          */}
                                        {subIdx > 0 && (
                                            <Divider sx={{ mx: 2, mt: 2.25, mb: 1.25, borderColor: C.borderLight }} />
                                        )}
                                        <Box
                                            onClick={() => sub.setOpen(v => !v)}
                                            sx={{
                                                display: "flex", alignItems: "center", gap: 0.7,
                                                // 헤더는 들여쓰지 않는다 — 들여쓰기는 아래 채널 항목만 갖는다.
                                                px: 2, py: 0.55, mb: 0.4, cursor: "pointer",
                                                "&:hover": { bgcolor: C.bg },
                                            }}
                                        >
                                            {/* 상태를 <b>색점</b> 대신 <b>색 폴더</b>로 — 이 줄이 곧 묶음(폴더)이라는 뜻이
                                                아이콘 모양에서 바로 읽히고, 6px 점보다 색도 훨씬 잘 보인다. */}
                                            <FolderRoundedIcon sx={{ fontSize: 16, color: sub.dot, flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: 12.5, color: C.textSecondary, fontWeight: 700, flex: 1, minWidth: 0 }}>
                                                {sub.label}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: "#cbd5e1", fontWeight: 600 }}>
                                                {sub.list.length}
                                            </Typography>
                                            {/* 토글은 오른쪽 끝 — 라벨의 왼쪽 기준선이 밀리지 않게.
                                                아이콘을 갈아치우지 않고 회전시킨다(SectionHeader 와 같은 규칙) —
                                                교체는 순간이동처럼 보여 아래 목록의 전이와 따로 논다. */}
                                            <ExpandLessRoundedIcon sx={{
                                                fontSize: 14, color: C.textMuted,
                                                transition: "transform 160ms ease",
                                                transform: sub.open ? "none" : "rotate(180deg)",
                                            }} />
                                        </Box>
                                        {/* 접힘 — 보고 있는 채널만(폴더 트리 포함. 접었다고 내가 보던
                                            폴더 범위까지 사라지면 어디를 보고 있었는지 단서가 끊긴다) */}
                                        <CollapseRows open={!sub.open && pinned.length > 0}>
                                            {pinned.map(c => (
                                                <React.Fragment key={c.id}>
                                                    <ChannelItem ctx={ctx} channel={c} dimmed={sub.dimmed} indented />
                                                    <FolderTree ctx={ctx} channel={c} />
                                                </React.Fragment>
                                            ))}
                                        </CollapseRows>
                                        {/* 펼침 — 구간 전체 */}
                                        <CollapseRows open={sub.open}>
                                            {sub.list.map(c => (
                                                <React.Fragment key={c.id}>
                                                    <ChannelItem ctx={ctx} channel={c} dimmed={sub.dimmed} indented />
                                                    <FolderTree ctx={ctx} channel={c} />
                                                </React.Fragment>
                                            ))}
                                        </CollapseRows>
                                    </Box>
                                );
                            })}
                        </>
                    );
                })()}

                {/* (제거됨) '공지' · '팀' 섹션 — 공지는 사내 게시판으로, 팀 채널은 폐지.
                    두 유형의 옛 채널은 아래 '기타' 에 모여 계속 열람된다. */}

                {otherChannels.length > 0 && (projectsAll.length > 0 || favoriteChannels.length > 0) && (
                    <TypeBoundary />
                )}

                {otherChannels.length > 0 && (
                    <>
                        <SectionHeader label="기타" count={otherChannels.length} />
                        {otherChannels.map(c => (
                            <React.Fragment key={c.id}>
                                <ChannelItem ctx={ctx} channel={c} indented />
                                <FolderTree ctx={ctx} channel={c} />
                            </React.Fragment>
                        ))}
                    </>
                )}

                {channels.length === 0 && (
                    <Box sx={{ textAlign: "center", pt: 6, color: C.textMuted }}>
                        <FolderSpecialRoundedIcon sx={{ fontSize: 36, mb: 1, opacity: 0.4 }} />
                        <Typography sx={{ fontSize: 13 }}>채널이 없습니다</Typography>
                    </Box>
                )}

                {/*
                  * 보관된 채널 — 목록 <b>맨 아래</b>에 고정. 보관 채널은 이 목록에 섞이지 않으므로
                  * (서버가 archived=false 만 준다) 여기가 유일한 진입 경로다. 이게 없으면
                  * 사용자에게 보관은 삭제와 구분되지 않는다.
                  *
                  * 이름을 «보관함» 에서 바꾼 이유 — 「함」은 상자(기능 이름)로 읽혀서, 위에 늘어선
                  * 채널 목록과 같은 종류의 것인지 아닌지가 애매했다. 「보관된 채널」이면 무엇이
                  * 들어 있는지가 그대로 이름이라 설명이 필요 없다.
                  *
                  * <b>가운데 정렬</b>이다 — 위 항목들과 왼쪽 기준선을 공유하지 않는다는 뜻이다.
                  * 이건 채널 한 줄이 아니라 «다른 화면으로 나가는 문» 이라, 같은 선에 세워 두면
                  * 목록의 마지막 항목처럼 읽힌다.
                  *
                  * 건수를 붙이지 않는 이유 — 세려면 목록을 항상 미리 불러와야 하는데, 자주 여는
                  * 화면이 아니라 매 진입마다 그 조회를 얹을 값이 없다.
                  */}
                <Box
                    onClick={() => history.push("/channel/archived")}
                    sx={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 0.8,
                        mt: 2, mx: 2, mb: 2, px: 1, py: 0.9,
                        borderTop: `1px solid ${C.borderLight}`, pt: 1.5,
                        cursor: "pointer", color: C.textMuted,
                        "&:hover": { color: C.textSecondary },
                    }}
                >
                    <Inventory2RoundedIcon sx={{ fontSize: 15, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>보관된 채널</Typography>
                </Box>
            </Box>
        </CollapsibleSidebar>
    );
}

export default ChannelSidebar;
